"use client"

import {
  createContext,
  useState,
  useContext,
  useEffect,
  useRef,
  ReactNode,
  useCallback,
  useMemo,
} from "react"

// Defines the shape of the context data for the state.
interface BoundaryStateContextType {
  messages: Record<string, string[]>
  listeningIds: string[]
}

// Defines the shape of the context data for the actions.
interface BoundaryActionsContextType {
  startStreaming: (id: string) => void
  stopStreaming: (id: string) => void
  cleanupMessages: (id: string) => void
  addListeningId: (id: string) => void
  removeListeningId: (id: string) => void
}

// Creates the two contexts.
const BoundaryStateContext = createContext<
  BoundaryStateContextType | undefined
>(undefined)
const BoundaryActionsContext = createContext<
  BoundaryActionsContextType | undefined
>(undefined)

// Custom hook to use the BoundaryStateContext.
export const useBoundaryState = () => {
  const context = useContext(BoundaryStateContext)
  if (!context) {
    throw new Error(
      "useBoundaryState must be used within a BoundaryStatusProvider",
    )
  }
  return context
}

// Custom hook to use the BoundaryActionsContext.
export const useBoundaryActions = () => {
  const context = useContext(BoundaryActionsContext)
  if (!context) {
    throw new Error(
      "useBoundaryActions must be used within a BoundaryStatusProvider",
    )
  }
  return context
}

// Provider component that encapsulates the state and logic.
export const BoundaryStatusProvider = ({
  children,
}: {
  children: ReactNode
}) => {
  const [messages, setMessages] = useState<Record<string, string[]>>({})
  const [listeningIds, setListeningIds] = useState<string[]>([])
  const eventSourcesRef = useRef<Map<string, EventSource>>(new Map())

  const addListeningId = useCallback((id: string) => {
    setListeningIds((prev) => (prev.includes(id) ? prev : [...prev, id]))
  }, [])

  const removeListeningId = useCallback((id: string) => {
    setListeningIds((prev) => prev.filter((prevId) => prevId !== id))
  }, [])

  const cleanupMessages = useCallback((id: string) => {
    setMessages((prev) => {
      const { [id]: _, ...rest } = prev
      return rest
    })
  }, [])

  // Stop streaming for a specific ID and clean up its state
  const stopStreaming = useCallback(
    (id: string) => {
      const eventSource = eventSourcesRef.current.get(id)
      if (eventSource) {
        eventSource.close()
        eventSourcesRef.current.delete(id)
      }
      cleanupMessages(id)
    },
    [cleanupMessages],
  )

  // Start streaming for a specific ID
  const startStreaming = useCallback(
    (id: string) => {
      if (eventSourcesRef.current.has(id)) return // Already streaming

      addListeningId(id)

      const eventSource = new EventSource(
        `/resources/boundaries/status?import_request_id=${id}`,
      )

      eventSource.onopen = () => {
        console.log(`EventSource connection opened for ${id}`)
      }

      eventSource.onmessage = (event) => {
        setMessages((prev) => ({
          ...prev,
          [id]: [...(prev[id] || []), event.data],
        }))
      }

      eventSource.addEventListener("close", () => {
        console.log(
          `EventSource connection cleanly closed by the server for ${id}.`,
        )
        eventSource.close()
        eventSourcesRef.current.delete(id)
      })

      eventSource.onerror = () => {
        // This handler will now only be called for actual network errors
        // or if the server is unreachable.
        console.error(
          `EventSource failed for ${id}. Current state: ${eventSource.readyState}`,
        )
        eventSource.close()
        eventSourcesRef.current.delete(id)
      }

      eventSourcesRef.current.set(id, eventSource)
    },
    [cleanupMessages, addListeningId],
  )

  // Cleanup all on unmount
  useEffect(() => {
    return () => {
      eventSourcesRef.current.forEach((es) => es.close())
      eventSourcesRef.current.clear()
    }
  }, [])

  const actionsValue = useMemo(
    () => ({
      startStreaming,
      stopStreaming,
      cleanupMessages,
      addListeningId,
      removeListeningId,
    }),
    [startStreaming, stopStreaming, cleanupMessages, addListeningId, removeListeningId],
  )

  const stateValue = useMemo(
    () => ({ messages, listeningIds }),
    [messages, listeningIds],
  )

  return (
    <BoundaryActionsContext.Provider value={actionsValue}>
      <BoundaryStateContext.Provider value={stateValue}>
        {children}
      </BoundaryStateContext.Provider>
    </BoundaryActionsContext.Provider>
  )
}
